import { CATEGORIES, localizedLabel, localizedToolTitle } from '../lib/toolCategories';
import { createIcons, icons } from 'lucide';
import enCommon from '../locales/en/common.json';
import frCommon from '../locales/fr/common.json';
import esCommon from '../locales/es/common.json';
import deCommon from '../locales/de/common.json';
import itCommon from '../locales/it/common.json';
import zhCommon from '../locales/zh/common.json';
import jaCommon from '../locales/ja/common.json';
import './topnav.css';

const TRANSLATIONS = { en: enCommon, fr: frCommon, es: esCommon, de: deCommon, it: itCommon, zh: zhCommon, ja: jaCommon };
const PRIMARY_CATEGORIES = ['organize', 'convert', 'edit', 'sign'];

function lookup(obj, dotted) {
  return dotted.split('.').reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), obj);
}

function tFn(lang) {
  const pri = TRANSLATIONS[lang] || TRANSLATIONS.en;
  return (key) => {
    const stripped = key.replace(/^common:/, '').replace(/^tools:/, '');
    return lookup(pri, stripped) ?? lookup(TRANSLATIONS.en, stripped) ?? null;
  };
}

function getCurrentLang() {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  return ['fr', 'es', 'de', 'it', 'zh', 'ja'].includes(first) ? first : 'en';
}

function readSession() {
  try {
    const raw = localStorage.getItem('scrivar_session');
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && data.email ? data : null;
  } catch { return null; }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);
}

document.addEventListener('DOMContentLoaded', () => {
  const mount = document.getElementById('topnav-mount');
  if (mount) renderTopNav(mount);
});

function buildMarkup(t, session) {
  const ctaSignedOut = `
    <a href="/login" class="btn btn-ghost">${escapeHtml(t('nav.login') || 'Log In')}</a>
    <a href="/pricing" class="btn btn-primary">${escapeHtml(t('nav.start_trial') || 'Free Trial')}</a>
  `;
  const ctaSignedIn = `
    <a href="/tools" class="btn btn-secondary">${escapeHtml(t('nav.open_app') || 'Open app')}</a>
    <div class="topnav-account-wrap" style="position: relative;">
      <button id="nav-account-btn" class="topnav-account-btn" type="button" aria-haspopup="menu" aria-expanded="false" aria-controls="nav-account-menu">
        <span class="topnav-avatar">${escapeHtml((session?.email?.[0] || '?').toUpperCase())}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <div id="nav-account-menu" class="topnav-account-menu hidden" role="menu">
        <div class="header">
          <div class="label">${escapeHtml(t('nav.signed_in_as') || 'Signed in as')}</div>
          <div class="email" data-auth-email>${escapeHtml(session?.email || '')}</div>
        </div>
        <a href="/account" role="menuitem">${escapeHtml(t('nav.my_account') || 'My account')}</a>
        <a href="/account/billing" role="menuitem">${escapeHtml(t('nav.billing') || 'Billing & plan')}</a>
        <a href="/cloud" role="menuitem">${escapeHtml(t('nav.cloud_documents') || 'Cloud documents')}</a>
        <a href="/activate" role="menuitem">${escapeHtml(t('nav.activate') || 'Activate code')}</a>
        <button type="button" role="menuitem" data-auth-signout>${escapeHtml(t('nav.sign_out') || 'Sign out')}</button>
      </div>
    </div>
  `;

  const primaryLinks = PRIMARY_CATEGORIES.map((id) => {
    const cat = CATEGORIES.find((c) => c.id === id);
    if (!cat) return '';
    return `<li><button type="button" class="topnav-link" data-category="${escapeHtml(id)}">${escapeHtml(localizedLabel(t, cat))}</button></li>`;
  }).join('');

  const dropdownCols = CATEGORIES.map((cat) => `
    <div class="topnav-col" data-cat-col="${escapeHtml(cat.id)}">
      <h5 style="--cat-fg: ${escapeHtml(cat.colors.fg)}">${escapeHtml(localizedLabel(t, cat))}</h5>
      <ul>
        ${cat.tools.map((tool) => `
          <li>
            <a href="${escapeHtml(tool.path || '/tools')}" class="topnav-tool"
               style="--tool-bg: ${escapeHtml(cat.colors.bg)}; --icon-bg: ${escapeHtml(cat.colors.bg)}; --icon-fg: ${escapeHtml(cat.colors.fg)}"
               title="${escapeHtml(tool.description || '')}">
              <span class="icon-wrap"><i data-lucide="${escapeHtml(tool.iconName)}"></i></span>
              <span class="label">${escapeHtml(localizedToolTitle(t, tool))}</span>
            </a>
          </li>
        `).join('')}
      </ul>
    </div>
  `).join('');

  const mobileSections = CATEGORIES.map((cat) => `
    <details>
      <summary>${escapeHtml(localizedLabel(t, cat))}<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg></summary>
      <div style="padding: 4px 8px 16px">
        ${cat.tools.map((tool) => `
          <a href="${escapeHtml(tool.path || '/tools')}" class="mobile-tool">
            <i data-lucide="${escapeHtml(tool.iconName)}" style="width: 14px; height: 14px; color: ${escapeHtml(cat.colors.fg)}"></i>
            ${escapeHtml(localizedToolTitle(t, tool))}
          </a>
        `).join('')}
      </div>
    </details>
  `).join('');

  const mobileAuth = session
    ? `<a href="/tools" class="mobile-link">${escapeHtml(t('nav.open_app') || 'Open app')}</a>
       <a href="/account" class="mobile-link">${escapeHtml(t('nav.my_account') || 'My account')}</a>
       <a href="/account/billing" class="mobile-link">${escapeHtml(t('nav.billing') || 'Billing & plan')}</a>
       <a href="/cloud" class="mobile-link">${escapeHtml(t('nav.cloud_documents') || 'Cloud documents')}</a>
       <a href="/activate" class="mobile-link">${escapeHtml(t('nav.activate') || 'Activate code')}</a>
       <button type="button" data-auth-signout class="mobile-link" style="width: 100%; text-align: left; background: transparent; border: none; cursor: pointer; font-family: inherit;">${escapeHtml(t('nav.sign_out') || 'Sign out')}</button>`
    : `<a href="/login" class="mobile-link">${escapeHtml(t('nav.login') || 'Log In')}</a>
       <a href="/pricing" class="mobile-link" style="color: var(--tn-primary); font-weight: 600">${escapeHtml(t('nav.start_trial') || 'Free Trial')}</a>`;

  return `
    <header class="topnav">
      <div class="topnav-inner">
        <a href="/" class="topnav-logo" aria-label="Scrivar home">
          <span class="mark">S</span><span>Scrivar</span>
        </a>
        <ul class="topnav-links">
          <li>
            <button type="button" class="topnav-tools-btn" aria-expanded="false">
              ${escapeHtml(t('nav.tools') || 'Tools')}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          </li>
          ${primaryLinks}
          <li><a href="/ai-pdf" class="topnav-link">AI PDF</a></li>
        </ul>
        <div class="topnav-spacer"></div>
        <div class="topnav-cta">
          <a href="/pricing" class="btn btn-ghost">${escapeHtml(t('nav.pricing') || 'Pricing')}</a>
          <a href="/teams/sales" class="btn btn-ghost">${escapeHtml(t('nav.teams') || 'Teams')}</a>
          ${session ? ctaSignedIn : ctaSignedOut}
        </div>
        <button type="button" class="topnav-hamburger" aria-label="Open menu" aria-expanded="false">
          <span></span><span></span><span></span>
        </button>
      </div>
      <div class="topnav-dropdown hidden" data-dropdown>
        <div class="topnav-dropdown-inner">${dropdownCols}</div>
        <div class="topnav-foot">
          <a href="/tools" class="all-tools">${escapeHtml(t('nav.view_all_tools') || 'View all 30+ tools')} →</a>
          <span class="free-note">${escapeHtml(t('nav.free_note') || 'Free to use · 3 tasks per day, no account required')}</span>
        </div>
      </div>
      <div class="topnav-mobile hidden" data-mobile>
        ${mobileSections}
        <a href="/pricing" class="mobile-link">${escapeHtml(t('nav.pricing') || 'Pricing')}</a>
        <a href="/teams/sales" class="mobile-link">${escapeHtml(t('nav.teams') || 'Teams')}</a>
        <a href="/ai-pdf" class="mobile-link">AI PDF</a>
        ${mobileAuth}
      </div>
    </header>
  `;
}

function renderTopNav(mount) {
  const lang = getCurrentLang();
  const t = tFn(lang);
  const session = readSession();
  const markup = buildMarkup(t, session);

  // Parse via DOMParser so script tags would not execute. All interpolated
  // values were already passed through escapeHtml() during string assembly.
  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  const headerEl = parsed.body.firstElementChild;
  if (!headerEl) return;
  mount.replaceChildren(headerEl);

  // Replace <i data-lucide="..."> placeholders with real lucide SVGs
  createIcons({ icons });

  const toolsBtn = mount.querySelector('.topnav-tools-btn');
  const dropdown = mount.querySelector('[data-dropdown]');
  const primaryBtns = mount.querySelectorAll('.topnav-link[data-category]');
  const hamburger = mount.querySelector('.topnav-hamburger');
  const mobile = mount.querySelector('[data-mobile]');

  function openDropdown(catId) {
    dropdown.classList.remove('hidden');
    toolsBtn.setAttribute('aria-expanded', 'true');
    mount.querySelectorAll('.topnav-col').forEach((col) => {
      col.dataset.active = (col.dataset.catCol === catId) ? 'true' : 'false';
    });
  }
  function closeDropdown() {
    dropdown.classList.add('hidden');
    toolsBtn.setAttribute('aria-expanded', 'false');
    mount.querySelectorAll('.topnav-col').forEach((col) => { col.dataset.active = 'false'; });
  }

  toolsBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (dropdown.classList.contains('hidden')) openDropdown(null); else closeDropdown();
  });
  primaryBtns.forEach((btn) => btn.addEventListener('click', (e) => {
    e.stopPropagation();
    openDropdown(btn.dataset.category);
  }));
  document.addEventListener('click', (e) => {
    if (!mount.contains(e.target)) closeDropdown();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDropdown(); });

  hamburger?.addEventListener('click', () => {
    const isHidden = mobile.classList.contains('hidden');
    mobile.classList.toggle('hidden');
    hamburger.setAttribute('aria-expanded', String(isHidden));
  });
}
